import React, { useState, memo, useEffect, useRef } from 'react';
import { Row, Col, Divider, Typography } from 'antd';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { FieldArray, useFormikContext } from 'formik';
import { useDispatch } from "react-redux";
import Swal from 'sweetalert2';
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { addLastContactedDate, setLastContactedDates } from "../../../Slices/lastContactedDatesSlice";
import { setIsRefetch } from '../../../Slices/salesSlice';
import { InputField, SelectInput, TextareaField, AntdButton, TimePickerField, DatePickerField } from '../../../blueplanitUtils/InputField';
import CommentField from './CommentField';
import { getOptionsByPath } from '../../../utilsdata';
import { findTimeZoneOption } from '../../../utils/timeZone';
import { SCHEDULING_CALL,TIMEZONE,TYPE_OF_CALL,AGENCY_INFORMATION} from '../../../Dropdownmanager';
dayjs.extend(utc);
dayjs.extend(timezone);

const SchedulingCall = memo((props) => {
  const dispatch = useDispatch();
  const { assignToUsers, setFieldValue } = props;
  const { values, handleChange, errors, touched } = useFormikContext();
  const isRefetch = useSelector((state) => state.sales.isRefetch);
  const lastContactedDates = useSelector(state => state.lastContactedDates);
  const NestedDatabucket = useSelector((state) => state.dataBucket.NestedDatabucket);
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails)
  const loginUserName = loginUser
    ? loginUser.firstname + " " + loginUser.lastname
    : '';
  const [emails, setEmails] = useState(values.leadsScheduledCalls?.map(call => call.emails) || []);
  const [phonenumbers, setPhonenumbers] = useState(values.leadsScheduledCalls?.map(call => call.phonenumber) || []);
  const [remarks, setRemarks] = useState(values.leadsScheduledCalls?.map(call => call.remarks) || []);
  const [localErrors, setLocalErrors] = useState([]);


  useEffect(() => {
    if (isRefetch) {
      setEmails([]);
      setPhonenumbers([]);
      setRemarks([]);
    }
    dispatch(setIsRefetch(false))
  }, [isRefetch])

  // 🔁 Sync local state when Formik values change (reinitialization)
  useEffect(() => {
    setEmails(values.leadsScheduledCalls?.map(call => call.emails) || []);
    setPhonenumbers(values.leadsScheduledCalls?.map(call => call.phonenumber) || []);
    setRemarks(values.leadsScheduledCalls?.map(call => call.remarks) || []);
  }, [values.leadsScheduledCalls]);

  // Debounced sync local state → Formik (prevents data loss on page navigation)
  const syncTimeoutRef = useRef(null);
  useEffect(() => {
    clearTimeout(syncTimeoutRef.current);
    syncTimeoutRef.current = setTimeout(() => {
      emails.forEach((value, index) => {
        if (values?.leadsScheduledCalls?.[index]?.emails !== value) setFieldValue(`leadsScheduledCalls[${index}].emails`, value, false);
      });
      phonenumbers.forEach((value, index) => {
        if (values?.leadsScheduledCalls?.[index]?.phonenumber !== value) setFieldValue(`leadsScheduledCalls[${index}].phonenumber`, value, false);
      });
      remarks.forEach((value, index) => {
        if (values?.leadsScheduledCalls?.[index]?.remarks !== value) setFieldValue(`leadsScheduledCalls[${index}].remarks`, value, false);
      });
    }, 200);
    return () => clearTimeout(syncTimeoutRef.current);
  }, [emails, phonenumbers, remarks, values?.leadsScheduledCalls, setFieldValue]);

  // Flush on unmount
  useEffect(() => {
    return () => {
      clearTimeout(syncTimeoutRef.current);
      emails.forEach((value, index) => {
        if (values?.leadsScheduledCalls?.[index]?.emails !== value) setFieldValue(`leadsScheduledCalls[${index}].emails`, value, false);
      });
      phonenumbers.forEach((value, index) => {
        if (values?.leadsScheduledCalls?.[index]?.phonenumber !== value) setFieldValue(`leadsScheduledCalls[${index}].phonenumber`, value, false);
      });
      remarks.forEach((value, index) => {
        if (values?.leadsScheduledCalls?.[index]?.remarks !== value) setFieldValue(`leadsScheduledCalls[${index}].remarks`, value, false);
      });
    };
  }, [emails, phonenumbers, remarks, values?.leadsScheduledCalls, setFieldValue]);

  const handleAddRow = (arrayHelpers) => {

    const newDate = dayjs();
    setFieldValue("lastContactedDate", newDate);
    arrayHelpers.push({
      date: newDate || "",
      time: dayjs().set('second', 0).set('millisecond', 0) || '',
      callType: '',
      emails: '',
      phonenumber: '',
      callScheduledBy: loginUserName,
      timeZone: null,
      remarks: '',
    });

    // Add the new date to Redux state
    dispatch(addLastContactedDate(newDate.toISOString()));
  };

  const handleDeleteRow = (index, arrayHelpers) => {

    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you really want to delete this Scheduling record?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        arrayHelpers.remove(index); // Remove the call record
        setEmails((prev) => prev.filter((_, i) => i !== index));
        setPhonenumbers((prev) => prev.filter((_, i) => i !== index));
        setRemarks((prev) => prev.filter((_, i) => i !== index));

        // Remove the corresponding local errors
        setLocalErrors((prev) => prev.filter((_, i) => i !== index));

        // Get the date of the row being deleted from `leadsCalls`
        const deletedRowDate = arrayHelpers.form.values.leadsScheduledCalls[index].date;

        const lastContactedDatess = lastContactedDates.filter(date => dayjs(date).valueOf() !== dayjs(deletedRowDate).valueOf());

        const latestDate = lastContactedDatess.length > 0
          ? Math.max(...lastContactedDatess.map(date => dayjs(date).valueOf()))
          : null;

        dispatch(setLastContactedDates(lastContactedDatess));

        if (latestDate) {
          setFieldValue("lastContactedDate", dayjs(latestDate));
        }
      }
    });

  };

  const getTime = (time) => {
    if (!time) return null;

    if (typeof time === "object" && dayjs.isDayjs(time)) {
      return time;
    }

    if (typeof time === "string") {
      const localTime = dayjs.utc(time).local();
      return localTime;
    }

    return null;
  };
  const getDateValue = (datevalue) => {
    let finalDate = null;
    if (datevalue !== undefined || datevalue !== '') {
      if (typeof datevalue === 'string' && datevalue?.length === 13) {
        finalDate = dayjs(parseInt(datevalue))
      } else if (typeof datevalue === 'object') {
        finalDate = datevalue
      }
    }
    return finalDate;
  }


  // Local Validation for Email (onChange)
  const handleEmailChange = (e, index) => {
    const email = e.target.value.trim();
    const newEmails = [...emails];
    newEmails[index] = email;
    let errorMessage = "";

    // Updated email validation (only for '.com' domain)
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9]{2,}$/;
    if (email && !emailRegex.test(email.trim())) {
      errorMessage = "Invalid email format";
    }

    // Remove phone error if email is provided
    const newErrors = [...localErrors];
    newErrors[index] = {
      ...newErrors[index],
      emails: errorMessage,
      phonenumber: email ? "" : newErrors[index]?.phonenumber, // Clear phone error if email is valid
    };

    setEmails(newEmails);
    setLocalErrors(newErrors);
  };

  // Local Validation for Phone Number (onChange)
  const handlePhoneNumberChange = (e, index) => {
    const newPhonenumbers = [...phonenumbers];
    newPhonenumbers[index] = e.target.value;
    setPhonenumbers(newPhonenumbers);
  };

  return (
    <div style={{ marginTop: '15px', backgroundColor: "#fff", padding: '20px', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)', borderRadius: '8px' }}>
      <Typography.Title level={4} >Schedule Track</Typography.Title>
      <FieldArray
        name="leadsScheduledCalls"
        render={(arrayHelpers) => (
          <>
            <Row justify="end" style={{ marginBottom: '8px' }}>
              <AntdButton
                label="Add Row"
                type="primary"
                onClick={() => handleAddRow(arrayHelpers)}
              />
            </Row>
            {values?.leadsScheduledCalls?.map((ScheduledCallInfo, index) => (
              <div key={index} style={{ marginBottom: '1rem' }}>
                <Row gutter={16}>
                  <Col span={24} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <Typography.Text strong>Call Schedule No. {index + 1}</Typography.Text>
                    <i
                      className="fa-regular fa-trash-can"
                      style={{
                        color: '#00c5fb',
                        cursor: 'pointer',
                        fontSize: '16px',
                      }}
                      onClick={() => handleDeleteRow(index, arrayHelpers)}
                    />
                  </Col>

                  <Col span={6}>
                    <DatePickerField
                      label="Date"
                      inputReadOnly={true}
                      allowClear={false}
                      format="DD/MM/YYYY"
                      name={`leadsScheduledCalls.${index}.date`}
                      value={getDateValue(values.leadsScheduledCalls?.[index]?.date)}
                      onChange={(date) => setFieldValue(`leadsScheduledCalls.${index}.date`, date)}
                      style={{ width: '100%' }}
                    />
                  </Col>

                  <Col span={6}>
                    <TimePickerField
                      label="Time"
                      name={`leadsScheduledCalls.${index}.time`}
                      value={
                        ScheduledCallInfo?.time && getTime(ScheduledCallInfo?.time)
                      }
                      onChange={(time) =>
                        handleChange({
                          target: { value: time, name: `leadsScheduledCalls.${index}.time` },
                        })
                      }
                      format="h:mm A"
                      allowClear={false}
                      inputReadOnly={true}
                      style={{ width: '100%' }}
                    />

                  </Col>

                  <Col span={6}>
                    <SelectInput
                      label="Type of Call"
                      style={{ width: "100%" }}
                      value={values.leadsScheduledCalls[index]?.callType || 'Phone Call'}
                      onChange={(value) => setFieldValue(`leadsScheduledCalls.${index}.callType`, value)}
                      options={getOptionsByPath(NestedDatabucket, [SCHEDULING_CALL, TYPE_OF_CALL])}
                      placeholder="Select Call Type"
                    />

                  </Col>

                  <Col span={6}>
                    <InputField
                      label="Emails"
                      name={`leadsScheduledCalls.${index}.emails`}
                      value={emails[index] || ''}
                      onChange={(e) => {
                        handleEmailChange(e, index); // Local validation for email
                      }}
                      onBlur={(e) => {
                        handleChange({
                          target: {
                            name: `leadsScheduledCalls[${index}].emails`,
                            value: e.target.value.trim(),
                          },
                        });
                      }}
                      error={
                        localErrors[index]?.emails ||
                        (touched?.leadsScheduledCalls?.[index]?.emails &&
                          errors?.leadsScheduledCalls?.[index]?.emails)
                      }
                    />
                  </Col>

                  <Col span={6}>
                    <InputField
                      label="Phone Number"
                      name={`leadsScheduledCalls.${index}.phonenumber`}
                      value={phonenumbers[index] || ''}
                      onChange={(e) => {
                        handlePhoneNumberChange(e, index); // Local validation for phone number
                      }}
                      onBlur={(e) => {
                        handleChange({
                          target: {
                            name: `leadsScheduledCalls[${index}].phonenumber`,
                            value: e.target.value.trim(),
                          },
                        });
                      }}
                    />
                  </Col>

                  <Col span={6}>
                    <SelectInput
                      label="Call Scheduled by"
                      style={{ width: "100%" }}
                      value={values.leadsScheduledCalls[index]?.callScheduledBy || loginUserName}
                      onChange={(value) => setFieldValue(`leadsScheduledCalls.${index}.callScheduledBy`, value)}
                      options={assignToUsers?.map((user) => ({
                        key: user.id,
                        label: `${user.firstname} ${user.lastname}`,
                        value: `${user.firstname} ${user.lastname}`
                      }))}
                      placeholder="Select User"
                      showSearch
                    />
                  </Col>

                  <Col span={6}>
                    <SelectInput
                      label="TimeZone"
                      style={{ width: "100%" }}
                      value={values.leadsScheduledCalls[index]?.timeZone || findTimeZoneOption(ScheduledCallInfo?.timeZone, getOptionsByPath(NestedDatabucket, [AGENCY_INFORMATION,TIMEZONE]))}
                      onChange={(value) => setFieldValue(`leadsScheduledCalls.${index}.timeZone`, value)}
                      options={getOptionsByPath(NestedDatabucket, [AGENCY_INFORMATION,TIMEZONE])}
                      placeholder="Select TimeZone"
                    />
                  </Col>

                  <Col span={24}>
                    <TextareaField
                      label="Remarks"
                      name={`leadsScheduledCalls.${index}.remarks`}
                      value={remarks[index] || ''}
                      onChange={(e) => {
                        const newRemarks = [...remarks];
                        newRemarks[index] = e.target.value;
                        setRemarks(newRemarks);
                      }}
                      onBlur={(e) => {
                        handleChange({
                          target: {
                            name: `leadsScheduledCalls[${index}].remarks`,
                            value: e.target.value,
                          },
                        });
                      }}

                    />
                  </Col>
                </Row>
                <Divider style={{ backgroundColor: '#d9d9d9', margin: '16px 0' }} />
              </div>
            ))}
          </>
        )}
      />
      <CommentField label="Comments" name="scheduledCallComments" />
    </div>
  );
});

export default SchedulingCall;

